import {
  faCheck,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RoutePlanFeedbackPackageDTO } from 'api/model';
import useTranslations from 'contexts/basicData/useTranslations';
import React from 'react';
import styled from 'styled-components';
import CandidateChoicePanel from '../Ruttplanering/CandidateChoicePanel';
import CaseRegLink from '../Ruttplanering/CaseRegLink';
import RouteMap from '../Ruttplanering/RouteMap';
import {
  defaultSelection,
  describeHubStopoverDestination,
  formatDurationMs,
  formatWholeDaySpan,
  hubHomewardMs,
  selectedHubStopover,
  wholeDayFor,
} from '../Ruttplanering/selection-helpers';
import { getManualReasonLabel } from '../Ruttplanering/warning-helpers';
import WarningPill from '../Ruttplanering/WarningPills';
import {
  describePlannedRemedy,
  describeRebookingEffect,
  describeRebookingMove,
  formatClock,
  isUnplanned,
} from './feedback-round-helpers';

const Card = styled.section`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 10px;
  padding: 16px;
  display: grid;
  gap: 14px;
  min-width: 0;
`;

const Head = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

const PackageTitle = styled.h2`
  margin: 0;
  font-size: 1.2rem;
`;

const Pill = styled.span<{ tone: 'manual' | 'neutral' }>`
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
  background: ${({ tone, theme }) =>
    tone === 'manual'
      ? 'rgba(255, 191, 0, 0.25)'
      : theme.colors.background.secondary};
`;

const Muted = styled.span`
  color: ${({ theme }) => theme.colors.foreground.tertiary};
  font-size: 0.88rem;
`;

const Legs = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0;
`;

const Leg = styled.li`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr) minmax(0, 220px);
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};

  @media (max-width: 760px) {
    grid-template-columns: 80px minmax(0, 1fr);
  }
`;

const Time = styled.span`
  font-variant-numeric: tabular-nums;
  font-size: 0.88rem;
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

const LegMain = styled.div`
  display: grid;
  gap: 2px;
  min-width: 0;
`;

const Reg = styled.span`
  font-weight: 600;
  margin-right: 8px;
`;

const Now = styled.div`
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.foreground.tertiary};

  @media (max-width: 760px) {
    grid-column: 2;
  }
`;

const Endpoint = styled.li`
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  color: ${({ theme }) => theme.colors.foreground.secondary};
  font-size: 0.9rem;

  @media (max-width: 760px) {
    grid-template-columns: 80px minmax(0, 1fr);
  }
`;

const NavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.background.primary};
  color: ${({ theme }) => theme.colors.foreground.primary};
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.35;
  }
`;

const Position = styled.span`
  color: ${({ theme }) => theme.colors.foreground.tertiary};
  font-size: 0.88rem;
  font-variant-numeric: tabular-nums;
`;

const AnsweredMark = styled.span`
  color: ${({ theme }) => theme.colors.foreground.good};
  font-size: 0.85rem;
  white-space: nowrap;
`;

const Rebookings = styled.div`
  display: grid;
  gap: 4px;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background.secondary};
  font-size: 0.9rem;
`;

const PillRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

/** Pilarna på sidan. Toasten visar ett paket i taget och skickar inte med dem. */
export interface PackageNavigation {
  /** 1-baserad plats i provets bläddringslista. */
  position: number;
  total: number;
  answered: boolean;
  /** Utelämnad i listans början respektive slut. */
  onPrevious?: () => void;
  onNext?: () => void;
}

interface Props {
  data: RoutePlanFeedbackPackageDTO;
  navigation?: PackageNavigation;
}

/**
 * Ett paket ur kvällens ruttprov, bara för läsning. Inga gap-val, inget "Byt fälttestare", ingen
 * verkställ — förslaget ska bedömas, inte planeras.
 */
const PackageView: React.FC<Props> = ({ data, navigation }) => {
  const translations = useTranslations();
  const pkg = data.package;
  const candidate = pkg.candidates[0];
  const states = new Map(data.currentLegStates.map((s) => [s.assignmentId, s]));
  // Serverns bedömning (samma krets som planeringens pool), inte bara statusen: ett låst ben eller
  // ett stängt ärende är också taget.
  const anyPlanned = data.currentLegStates.some((s) => !s.isOpenForPlanning);
  // Samma förval som planeringsvyn, så att det som bedöms är det som hade verkställts.
  const selection = defaultSelection(pkg);
  const hubStopover = selectedHubStopover(candidate, selection, data.driverSwap);
  // Dagen med ut- och hemresa (Feedbackknapp-2, -3), som raden "Hela dagen" i paketkortet.
  const wholeDay = candidate ? wholeDayFor(candidate, hubStopover) : null;
  const lastLegId = pkg.legs[pkg.legs.length - 1]?.assignmentId;
  const firstRemedy = describePlannedRemedy(
    selection.firstGapChoice,
    candidate?.firstLegGap
  );
  const lastRemedy = describePlannedRemedy(
    selection.lastGapChoice,
    candidate?.lastLegGap
  );

  return (
    <Card aria-label={`Ruttförslag paket ${pkg.packageIndex}`}>
      <Head>
        {navigation && (
          <NavButton
            type="button"
            aria-label="Föregående förslag"
            disabled={!navigation.onPrevious}
            onClick={navigation.onPrevious}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </NavButton>
        )}
        <PackageTitle>Paket #{pkg.packageIndex}</PackageTitle>
        {navigation && (
          <>
            <Position>
              {navigation.position} av {navigation.total}
            </Position>
            <NavButton
              type="button"
              aria-label="Nästa förslag"
              disabled={!navigation.onNext}
              onClick={navigation.onNext}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </NavButton>
            {navigation.answered && (
              <AnsweredMark>
                <FontAwesomeIcon icon={faCheck} /> Besvarat
              </AnsweredMark>
            )}
          </>
        )}
        <Pill tone="neutral">{pkg.legs.length} uppdrag</Pill>
        {wholeDay && (
          <Pill tone="neutral">
            Hela dagen {wholeDay.hours.toFixed(1).replace('.', ',')} h
          </Pill>
        )}
      </Head>

      {candidate ? (
        <div>
          <b>{candidate.fullName}</b>
          {candidate.isFullTime ? ' · heltid' : ''}
          {candidate.startAnchorName
            ? ` · startar vid ${candidate.startAnchorName}`
            : ''}
          {wholeDay && (
            <>
              <br />
              <Muted>
                Hela dagen {formatWholeDaySpan(wholeDay)}, med ut- och hemresa
                {wholeDay.existingAssignments > 0
                  ? ` och ${wholeDay.existingAssignments} uppdrag föraren redan har`
                  : ''}
                .
              </Muted>
            </>
          )}
          {/* Varför hen, och vilka andra som fanns — utan det går valet inte att bedöma
              rättvist (Feedbackknapp-1, 2026-10-28). Äldre prov saknar förklaringen och visar
              då bara vem som var näst bäst. */}
          {pkg.candidateChoice ? (
            <CandidateChoicePanel
              defaultOpen
              packageLabel={(index) => `paket #${index}`}
              pkg={pkg}
            />
          ) : (
            pkg.candidates.length > 1 && (
              <>
                <br />
                <Muted>
                  Föreslagen av {pkg.candidates.length} möjliga fälttestare.
                  Näst bäst: {pkg.candidates[1].fullName}.
                </Muted>
              </>
            )
          )}
        </div>
      ) : (
        <Muted>Ingen fälttestare kunde föreslås.</Muted>
      )}

      {(pkg.warnings.length > 0 || pkg.manualReasons.length > 0) && (
        <PillRow>
          {pkg.warnings.map((w) => (
            <WarningPill key={`w-${w}`} warning={w} />
          ))}
          {pkg.manualReasons.map((r) => (
            <Pill key={`m-${r}`} tone="manual">
              {getManualReasonLabel(r)}
            </Pill>
          ))}
        </PillRow>
      )}

      <RouteMap
        packages={[pkg]}
        hoveredPackageIndex={pkg.packageIndex}
        selections={{ [pkg.packageIndex]: selection }}
        driverSwaps={data.driverSwap ? [data.driverSwap] : undefined}
      />

      <Legs>
        {candidate?.commuteToFirst && (
          <Endpoint>
            <Time>Utresa</Time>
            <span>
              Från {candidate.startAnchorName ?? 'hemankaret'} ·{' '}
              {formatDurationMs(candidate.commuteToFirst.durationMs)} ·{' '}
              {Math.round(candidate.commuteToFirst.distanceKm)} km
              {candidate.firstLegGap && !firstRemedy
                ? ' · behöver tåg, annons eller utfyllnad'
                : ''}
              {firstRemedy && (
                <>
                  <br />
                  <Muted>{firstRemedy}</Muted>
                </>
              )}
            </span>
          </Endpoint>
        )}
        {!candidate?.commuteToFirst && firstRemedy && (
          // Utan pendlingsestimat (t.ex. start från en övernattning) ska luckvalet ändå synas.
          <Endpoint>
            <Time>Utresa</Time>
            <Muted>{firstRemedy}</Muted>
          </Endpoint>
        )}
        {pkg.legs.map((leg, i) => {
          const state = states.get(leg.assignmentId);
          const transferRemedy = describePlannedRemedy(
            selection.transferGapChoices[leg.assignmentId] ?? 'none',
            leg.transferGap
          );
          return (
            <Leg key={leg.assignmentId}>
              <Time>
                {formatClock(leg.plannedStart)}–
                {formatClock(
                  hubStopover && leg.assignmentId === lastLegId
                    ? hubStopover.arrivalAtHub
                    : leg.plannedEnd
                )}
              </Time>
              <LegMain>
                <span>
                  <Reg>
                    <CaseRegLink caseId={leg.caseId}>
                      {leg.registrationNumber || `Uppdrag ${leg.assignmentId}`}
                    </CaseRegLink>
                  </Reg>
                  {translations.caseTypes?.[leg.caseTypeID]}
                </span>
                <span>
                  {leg.fromCity} →{' '}
                  {hubStopover && leg.assignmentId === lastLegId
                    ? describeHubStopoverDestination(hubStopover)
                    : leg.toCity}
                </span>
                {i > 0 && leg.transferFromPreviousKm > 0 && (
                  <Muted>
                    Överföring från föregående:{' '}
                    {Math.round(leg.transferFromPreviousKm)} km,{' '}
                    {formatDurationMs(leg.transferFromPreviousMs)}
                    {leg.transferNeedsPersonalTransport
                      ? ' · kräver tåg/buss'
                      : ''}
                    {leg.transferTransit?.description && !transferRemedy
                      ? ` · ${leg.transferTransit.description}`
                      : ''}
                  </Muted>
                )}
                {transferRemedy && <Muted>{transferRemedy}</Muted>}
              </LegMain>
              <Now>
                {state
                  ? state.isOpenForPlanning
                    ? 'Just nu: oplanerat'
                    : isUnplanned(state.status)
                    ? 'Just nu: låst för manuell planering eller avslutat'
                    : `Just nu: ${translations.assignmentStatuses?.[state.status] ?? 'planerat'}${
                        state.assignedToName ? ` · ${state.assignedToName}` : ''
                      }`
                  : ''}
              </Now>
            </Leg>
          );
        })}
        {hubStopover && (
          // Bilen står på hubben, så hemresan går därifrån (hubbförslagets egen hemkomst).
          <Endpoint>
            <Time>Hemresa</Time>
            <span>
              Från {hubStopover.hubName} ·{' '}
              {formatDurationMs(hubHomewardMs(hubStopover))}{' '}
              · hemma {formatClock(hubStopover.arrivalHomeWithStopover)}
              {lastRemedy && (
                <>
                  <br />
                  <Muted>{lastRemedy}</Muted>
                </>
              )}
            </span>
          </Endpoint>
        )}
        {!hubStopover && candidate?.returnFromLast && (
          <Endpoint>
            <Time>Hemresa</Time>
            <span>
              Till {candidate.endAnchorName ?? 'hemankaret'} ·{' '}
              {formatDurationMs(candidate.returnFromLast.durationMs)} ·{' '}
              {Math.round(candidate.returnFromLast.distanceKm)} km
              {candidate.lastLegGap && !lastRemedy
                ? ' · behöver tåg, annons eller hotell'
                : ''}
              {lastRemedy && (
                <>
                  <br />
                  <Muted>{lastRemedy}</Muted>
                </>
              )}
            </span>
          </Endpoint>
        )}
        {!hubStopover && !candidate?.returnFromLast && lastRemedy && (
          <Endpoint>
            <Time>Hemresa</Time>
            <Muted>{lastRemedy}</Muted>
          </Endpoint>
        )}
      </Legs>

      {/* Äldre prov sparades innan förslagen fanns i förslaget. */}
      {(pkg.rebookingProposals ?? []).length > 0 && (
        <Rebookings aria-label="Föreslagna ombokningar">
          <b>Föreslagen ny tid — trafikledaren ringer och godkänner</b>
          {pkg.rebookingProposals.map((r) => (
            <span key={r.assignmentId}>{describeRebookingMove(r)}</span>
          ))}
          <Muted>{describeRebookingEffect(pkg.rebookingProposals)}</Muted>
        </Rebookings>
      )}

      {anyPlanned && (
        <Muted>
          &quot;Just nu&quot; visar hur uppdragen faktiskt ligger. Förslaget har
          inte påverkat det.
        </Muted>
      )}
    </Card>
  );
};

export default PackageView;
